import { Box, Dialog, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

interface AIFieldComparison {
  field: string;
  reasoning: string;
}

interface MatchReasoningPopupProps {
  open: boolean;
  handleClose: () => void;
  /** The match object (flat list shape has mentee/mentor; grouped shape has `user`). */
  match: any;
}

const scoreColorFor = (score: number) =>
  score >= 85 ? '#4CAF50' : score >= 70 ? '#F79420' : '#B6B6B6';

/**
 * Read-only view of the AI reasoning stored on a single match (score, overall
 * assessment, per-field comparisons). Opened from the Matches 3-dots menu so an
 * admin can review why this specific pairing was suggested — before or after
 * confirming. Only rendered for matches that were created from an AI suggestion.
 */
function MatchReasoningPopup({ open, handleClose, match }: MatchReasoningPopupProps) {
  const score = typeof match?.aiScore === 'number' ? match.aiScore : null;
  const assessment: string = match?.aiOverallAssessment || '';
  const comparisons: AIFieldComparison[] = Array.isArray(match?.aiFieldComparisons)
    ? match.aiFieldComparisons
    : [];

  const mentorName = match?.mentor?.displayName || match?.mentor?.name;
  const menteeName = match?.mentee?.displayName || match?.mentee?.name;
  const counterpartName = match?.user?.displayName || match?.user?.name;
  const pairLabel =
    [mentorName, menteeName].filter(Boolean).join('  ✕  ') || counterpartName || '';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: { background: '#1A1A1A', borderRadius: '12px', border: '1px solid #2A2A2A' }
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: '16px 20px',
          borderBottom: '1px solid #2A2A2A'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Typography sx={{ color: '#FDFDFD', fontSize: 16, fontWeight: 700 }}>
            Match reasoning
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small" sx={{ color: '#B6B6B6' }}>
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      <Box
        sx={{
          p: '20px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          maxHeight: 460,
          overflowY: 'auto'
        }}
      >
        {(pairLabel || score !== null) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            {pairLabel && (
              <Typography sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 600 }}>
                {pairLabel}
              </Typography>
            )}
            {score !== null && (
              <Box
                sx={{
                  px: '8px',
                  py: '2px',
                  borderRadius: '20px',
                  border: `1px solid ${scoreColorFor(score)}`,
                  background: `${scoreColorFor(score)}14`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <Box sx={{ fontSize: 10 }}>✨</Box>
                <Box sx={{ color: scoreColorFor(score), fontSize: 12, fontWeight: 600 }}>
                  {score}%
                </Box>
              </Box>
            )}
          </Box>
        )}

        {assessment && (
          <Box sx={{ p: '12px', borderRadius: '6px', background: '#0F0F0F', border: '1px solid #2A2A2A' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', mb: '4px' }}>
              <Box sx={{ fontSize: 12 }}>✨</Box>
              <Box sx={{ color: '#F79420', fontSize: 11, fontWeight: 600 }}>Overall assessment</Box>
            </Box>
            <Box sx={{ color: '#B6B6B6', fontSize: 12, lineHeight: 1.5 }}>{assessment}</Box>
          </Box>
        )}

        {comparisons.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {comparisons.map((cmp, idx) => (
              <Box
                key={`${cmp.field}-${idx}`}
                sx={{ p: '10px', borderRadius: '6px', background: '#0F0F0F', border: '1px solid #2A2A2A' }}
              >
                <Box sx={{ color: '#FDFDFD', fontSize: 12, fontWeight: 600, mb: '2px' }}>
                  {cmp.field}
                </Box>
                <Box sx={{ color: '#B6B6B6', fontSize: 12, lineHeight: 1.5 }}>{cmp.reasoning}</Box>
              </Box>
            ))}
          </Box>
        )}

        {!assessment && comparisons.length === 0 && (
          <Typography sx={{ color: '#B6B6B6', fontSize: 13 }}>
            Elliott&apos;s reasoning is not available for this match.
          </Typography>
        )}
      </Box>
    </Dialog>
  );
}

export default MatchReasoningPopup;
